/**
 * useLogoMaps.js — Shared hook for reference data lookups.
 *
 * Builds lookup maps from the sportsbooks and teams reference tables
 * (sent once on connect), so individual components can resolve logos,
 * team names, and abbreviations without embedding them in every record.
 *
 * Usage:
 *   const { bookLogos, teamMap } = useLogoMaps();
 *   const homeLogo = teamMap[row.homeTeamId]?.logo || '';
 *   const homeAbbr = teamMap[row.homeTeamId]?.abbreviation || '';
 *   const homeName = teamMap[row.homeTeamId]?.name || '';
 *   const bookLogo = bookLogos[row.bookName] || '';
 */

import { useMemo } from 'react';
import { useStoreData } from './useStoreData';

export function useLogoMaps() {
  const sportsbooks = useStoreData('sportsbooks');
  const teams = useStoreData('teams');

  return useMemo(() => {
    const bookLogos = {};
    for (const sb of (sportsbooks || [])) {
      if (sb.name) bookLogos[sb.name] = sb.logo || '';
    }
    // Logo overrides (when CDN logos are low quality or wrong)
    bookLogos['FanDuel'] = '/fanduel-logo.png';

    const teamMap = {};
    for (const t of (teams || [])) {
      if (t.id) teamMap[t.id] = t;
    }
    return { bookLogos, teamMap };
  }, [sportsbooks, teams]);
}